import _ from 'underscore';
import React, { useCallback, useMemo } from 'react';
import { Activity, Message } from 'botframework-directlinejs';
import { makeStyles } from '@fluentui/react-components';
import { AIMessage, PluginFunctions, PluginScenarios } from '@bingads-webui/ads-copilot-ui';
import { useI18nContext } from '@bingads-webui/copilot-i18n';
import { type ActivityRenderer } from '@bingads-webui-react/msa-copilot-common';

import { InsightsPanelPreview } from '../insights-panel-preview';
import { InsightsMissingParams } from '../insights-missing-params';

export const useFluctuationsStyles = makeStyles({
  message: {
    maxWidth: '100%',
  },
});

export const FluctuationsPluginRenderer: ActivityRenderer = React.memo((props) => {
  const {
    activity,
    logger,
    onFeedbackButtonClick,
    onFullInsightsClick,
    params: {
      action,
      onReferenceClick,
    },
    onMissingParamsSubmit,
    isReportingInsights15Enabled,
  } = props;

  const i18n = useI18nContext();
  const styles = useFluctuationsStyles();

  const handleFullInsightsClick = useCallback((ev: React.MouseEvent<HTMLButtonElement>, replyToId?: string) => {
    // invoke the full insights experience, provide the feedback button handler so we can also invoke feedback from there
    onFullInsightsClick?.(ev, replyToId, { onFeedbackButtonClick, i18n });
  }, [i18n, onFeedbackButtonClick, onFullInsightsClick]);

  const getEligibleCampaigns = useCallback((act) => {
    try {
      const detail = _.get(act, ['Result', 'Detail']);
      const parsedDetail = detail && JSON.parse(detail);

      const eligibleCampaigns = _.get(parsedDetail, ['EligibleCampaigns']);
      return _.map(eligibleCampaigns, c => ({ id: c.id || c.Id, name: c.name || c.Name }));
    } catch (e) {
      logger?.trace(`Error parsing JSON: ${e}`);
      return [];
    }
  }, []);

  const noEligibleCampaignsMessage = useMemo<Message>(() => {
    const text = i18n?.getString(_TL_('We\'re unable to provide performance insights for this query. This feature currently supports a limited set of scenarios for performance analysis. We are actively working on enhancing this feature\'s capabilities. For further assistance, please contact a Microsoft Advertising expert.'));

    return {
      text,
      value: {
        StreamingStatus: 'Completed',
      },
    } as Message;
  }, [i18n]);

  if (activity.type === 'event') {
    if (isReportingInsights15Enabled) {
      const missingParamsActivity = _.find(activity.value, (pluginResult) => pluginResult.Status === 'MissingParameters');
      if (!_.isEmpty(missingParamsActivity)) {
        const eligibleCampaigns = getEligibleCampaigns(missingParamsActivity);
        const getCampaigns = () => Promise.resolve(eligibleCampaigns);

        if (!_.isEmpty(eligibleCampaigns)) {
          return (
            <InsightsMissingParams
              getCampaigns={getCampaigns}
              logger={logger}
              replyToId={(activity as any).replyToId}
              onMissingParamsSubmit={onMissingParamsSubmit}
              pluginName={PluginScenarios.CampaignFluctuation}
              cardText={i18n?.getString(_TL_('Please confirm your campaign name (where fluctuations are detected) and the date range to view fluctuations.'))}
            />
          );
        }
        return (
          <AIMessage
            className="no-eligible-campaigns"
            message={noEligibleCampaignsMessage}
            showCopyButton={false}
            showFeedback
          />
        );
      }
    }

    // don't render the plugin results event as a separate card, InsightsPanelPreview will handle it
    return null;
  }

  if (activity.type === 'message') {
    // don't render anything if the fluctuations plugin failed to execute
    const features = (activity.value as any)?.Features;
    if (!_.any(features, (feature) => feature === PluginFunctions.GetAccountFluctuations || feature === PluginFunctions.GetCampaignFluctuations)) {
      return null;
    }
  }

  return (
    <AIMessage
      className={styles.message}
      message={activity as Message}
      onFeedbackButtonClick={onFeedbackButtonClick}
      action={action}
      onReferenceClick={onReferenceClick}
      showReferences={false}
      showDataDisclaimer
    >
      <p>
        <strong>{i18n?.getString(_TL_('Key insights'))}</strong>
      </p>
      <InsightsPanelPreview
        activity={activity}
        logger={logger}
        onFullInsightsClick={handleFullInsightsClick}
      />
    </AIMessage>
  );
});

FluctuationsPluginRenderer.canRender = (activity: Activity) => {
  if (activity.type === 'message') {
    const pluginName = (activity.value as any)?.PluginContext?.Name;

    // only render fluctuations card if user intent is present
    return pluginName === PluginScenarios.AccountFluctuation || pluginName === PluginScenarios.CampaignFluctuation;
  }

  if (activity.type === 'event' && activity.name === 'pluginResults') {
    return _.any(activity.value, (pluginResult) => pluginResult.Name === PluginScenarios.AccountFluctuation || pluginResult.Name === PluginScenarios.CampaignFluctuation || pluginResult.Name === PluginScenarios.PerformanceTimeSeries);
  }

  return false;
};
